import {useEffect, useRef} from 'react';
import * as echarts from 'echarts';
import {useAppStore} from '@/store/appStore.ts';
import {formatBatch} from '@/utils/format.ts';
import type {ProductMetric} from '@/types';

const PRODUCT_COLORS = [
    '#e5e7eb',
    '#cbd5e1',
    '#94a3b8',
    '#64748b',
    '#475569',
];

export const ProductChart = ({metrics, chartMode}: {
    metrics: ProductMetric[];
    chartMode: 'vol' | 'val';
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const chartRef = useRef<echarts.ECharts | null>(null);
    const selectExchange = useAppStore((s) => s.selectExchange);

    useEffect(() => {
        if (!ref.current) return;
        const chart = echarts.init(ref.current);
        chartRef.current = chart;
        const resize = () => chart.resize();
        const observer = new ResizeObserver(resize);
        observer.observe(ref.current);
        window.addEventListener('resize', resize);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', resize);
            chart.dispose();
            chartRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!chartRef.current) return;
        const months = [...new Set(metrics.map((m) => m.yearMonth))].sort();
        const products = [...metrics]
            .sort((a, b) => a.sortOrder - b.sortOrder || a.productName.localeCompare(b.productName))
            .map((m) => m.productName)
            .filter((n, i, arr) => arr.indexOf(n) === i);
        const map = new Map(metrics.map((m) => [m.yearMonth + '|' + m.productName, m]));
        const mode = chartMode === 'vol' ? 'Vol' : `Val (K,${selectExchange?.show_char})`;

        chartRef.current.setOption({
            tooltip: {
                trigger: 'item',
                backgroundColor: 'rgba(220,38,38,0.92)',
                borderColor: 'transparent',
                textStyle: {color: '#fff', fontSize: 11},
                formatter: (params: any) => {
                    const d = params.data || {};
                    const value = Number(d.value || 0).toLocaleString();
                    return `<div>
                        <div style="font-weight:700;color:#fecaca">${params.seriesName}</div>
                        ${mode}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${value}
                        <br/>
                        BIZ (%)&nbsp;&nbsp;&nbsp;&nbsp;${d.bizPct == null ? '--' : Number(d.bizPct).toFixed(1)}
                    </div>`;
                },
            },
            legend: {
                data: products,
                bottom: 0,
                left: 'center',
                icon: 'rect',
                itemWidth: 10,
                itemHeight: 10,
                itemGap: 8,
                textStyle: {fontSize: 8, color: '#64748b'},
            },
            grid: {left: 35, right: 15, top: 15, bottom: 55},
            xAxis: {
                type: 'category',
                data: months,
                axisLabel: {fontSize: 9, color: '#94a3b8', formatter: formatBatch},
                axisLine: {lineStyle: {color: '#e2e8f0'}},
            },
            yAxis: {
                type: 'value',
                axisLabel: {fontSize: 9, color: '#94a3b8'},
                splitLine: {lineStyle: {color: '#f1f5f9'}},
            },
            series: products.map((name, idx) => ({
                name,
                type: 'bar',
                stack: 'total',
                barWidth: '50%',
                data: months.map((month) => {
                    const row = map.get(month + '|' + name);
                    return {
                        value: chartMode === 'vol' ? (row?.volValue ?? 0) : ((row?.valValue ?? 0) / 1000).toFixed(1),
                        bizPct: chartMode === 'vol' ? (row?.volBizPct ?? null) : (row?.valBizPct ?? null),
                    };
                }),
                itemStyle: {color: PRODUCT_COLORS[idx % PRODUCT_COLORS.length]},
            })),
        });
    }, [metrics, chartMode, selectExchange]);

    return <div ref={ref} style={{width: '100%', height: '100%'}}/>;
};
